import React, { Dispatch, SetStateAction } from "react";

import { OperationVariables, QueryLazyOptions } from "@apollo/client";

import { BloomreachSearchArticlesByKeywordVariables } from "~/bff/transport/BloomreachSearchArticlesByKeyword";
import { BloomreachSearchByKeywordVariables } from "~/bff/transport/BloomreachSearchByKeyword";
import { CategoryNavItemVariables } from "~/bff/transport/CategoryNavItem";
import { BreadcrumbsItem } from "~/bff/types/BreadcrumbsItem";
import { Docs } from "~/bff/types/Docs";
import { Store } from "~/bff/types/Store";
import {
  AppliedFilters,
  Filters,
} from "~/components/products-layout/components/products-filters/types";
import { LoadingDirection } from "~/types/requestHandlers";

import {
  EntityFilterQuery,
  EntityFilterQueryParams,
  InputMaybe,
} from "__generated__/globalTypes";

import { Nullable, Optional } from "./general.types";

export interface IHandlerParams {
  appliedFilters?: Nullable<AppliedFilters>;
  activeSortingParam: string;
  isShopFilter: boolean | null;
  currentStore: Optional<Store> | null;
  isUiLoading?: boolean;
  pageSize: number;
  itemsMap: Map<string, number>;
  currentPageNumber: React.RefObject<number>; // remove mutable type
  direction: LoadingDirection;
  productsMarkerId: string | null;
  setProductsMarkerId: Dispatch<SetStateAction<string | null>>;
  efq?: EntityFilterQuery | null;
  appliedFilterOnClick?: [string, number | boolean];
  GAdata?: {
    breadcrumbs: Nullable<Nullable<BreadcrumbsItem>[]> | undefined;
  };
}

export enum IUseRequestHandlerActionsKind {
  UPDATE_MAP_NEXT = "UPDATE_MAP_NEXT",
  UPDATE_MAP_PREVIOUS = "UPDATE_MAP_PREVIOUS",
  CLEAR_MAP = "CLEAR_MAP",
  SET_ITEMS = "SET_ITEMS",
  SET_NUM_FOUND = "SET_NUM_FOUND",
  UPDATE_ON_COMPLETED = "UPDATE_ON_COMPLETED",
  SET_FILTERS = "SET_FILTERS",
  UPDATE_SORTING = "UPDATE_SORTING",
  UPDATE_APPLIED_FILTER_ON_CLICK = "UPDATE_APPLIED_FILTER_ON_CLICK",
  CLEAR_APPLIED_FILTERS = "CLEAR_APPLIED_FILTERS",
  FULL_OVERRIDE = "FULL_OVERRIDE",
  UPDATE_IS_SHOP_FILTER_SELECTED = "UPDATE_IS_SHOP_FILTER_SELECTED",
  REMOVE_APPLIED_FILTER_ON_CLICK = "REMOVE_APPLIED_FILTER_ON_CLICK",
  SET_DIRECTION = "SET_DIRECTION",
}

export interface IOnCompletedPayload {
  itemsMap: Map<string, number>;
  filters: Filters;
  numFound: Nullable<number>;
  loadedItems: Nullable<Nullable<Docs>[]>;
  newItems: Nullable<Nullable<Docs>[]>;
  currentPage: number;
  shouldConcat: boolean;
  direction: LoadingDirection;
  setNumFound: Dispatch<SetStateAction<Nullable<number | undefined>>>;
  setShouldTriggerGAFilterEvent: Dispatch<SetStateAction<boolean>>;
  isUiLoading?: boolean;
}

export interface IUpdateDirection {
  direction: LoadingDirection;
}

export interface SalesChanel {}

export interface Variables
  extends Partial<
    CategoryNavItemVariables &
      BloomreachSearchArticlesByKeywordVariables &
      BloomreachSearchByKeywordVariables
  > {
  q: string | undefined;
  efq?: {
    salesChannel?: InputMaybe<EntityFilterQueryParams>;
  } | null;
  start?: number;
  rows?: number;
  shouldConcat?: boolean;
  [k: string]: unknown;
}

interface IHashFilterParamsConfig {
  pageNumber?: number;
  getMoreItems: (
    options?: QueryLazyOptions<
      BloomreachSearchByKeywordVariables & OperationVariables
    >,
  ) => void;
  variables: Variables;
}

export enum ProductRequestType {
  Empty = "",
  AllProducts = "allProducts",
  ClickPlusCollectProducts = "clickPlusCollectProducts",
  ProductsInStore = "productsInStore",
  DefaultProductType = "defaultProductType",
}

export interface IHashFilterParams {
  activeSortingParam?: string;
  appliedFilters?: AppliedFilters;
  isShopFilter?: boolean | null;
  selectedStoreId?: string | undefined;
  config: IHashFilterParamsConfig;
  productType?: ProductRequestType;
}
export interface IUpdateFilterOnClick {
  appliedFilter: [string, number | boolean];
  updateFunc: (props: IHashFilterParams) => void;
  params: IHashFilterParams;
  GAdata?: {
    breadcrumbs: Nullable<Nullable<BreadcrumbsItem>[]> | undefined;
  };
}
export interface IUpdateSorting {
  sortingParam: string;
  updateFunc: (props: IHashFilterParams) => void;
  params: IHashFilterParams;
}
export interface IOverrideAppliedFilters {
  appliedFilters: AppliedFilters;
  updateFunc: (props: IHashFilterParams) => void;
  params: IHashFilterParams;
}
export interface IFullOverridePayload {
  props: {
    appliedFilters: AppliedFilters;
    activeSortingParam: string;
    isShopFilter: boolean | null;
    isUiLoading?: boolean;
  };
  updateFunc: (props: IHashFilterParams) => void;
  params: IHashFilterParams;
}

export interface IUpdateIsShopFilter {
  isShopFilter: boolean | null;
  selectedStore: Store;
}
export interface IUseRequestHandlerPayload {
  [IUseRequestHandlerActionsKind.UPDATE_MAP_NEXT]: [string, number][];
  [IUseRequestHandlerActionsKind.UPDATE_MAP_PREVIOUS]: [string, number][];
  [IUseRequestHandlerActionsKind.CLEAR_MAP]: [];
  [IUseRequestHandlerActionsKind.SET_ITEMS]: Docs[];
  [IUseRequestHandlerActionsKind.SET_NUM_FOUND]: number;
  [IUseRequestHandlerActionsKind.SET_FILTERS]: Filters;
  [IUseRequestHandlerActionsKind.UPDATE_SORTING]: IUpdateSorting;
  [IUseRequestHandlerActionsKind.UPDATE_APPLIED_FILTER_ON_CLICK]: IUpdateFilterOnClick;
  [IUseRequestHandlerActionsKind.REMOVE_APPLIED_FILTER_ON_CLICK]: IUpdateFilterOnClick;
  [IUseRequestHandlerActionsKind.CLEAR_APPLIED_FILTERS]: null;
  [IUseRequestHandlerActionsKind.UPDATE_ON_COMPLETED]: IOnCompletedPayload;
  [IUseRequestHandlerActionsKind.FULL_OVERRIDE]: IFullOverridePayload;
  [IUseRequestHandlerActionsKind.UPDATE_IS_SHOP_FILTER_SELECTED]: IUpdateIsShopFilter;
  [IUseRequestHandlerActionsKind.SET_DIRECTION]: IUpdateDirection;
}

export interface IUseRequestHandlerActions {
  type: IUseRequestHandlerActionsKind;
  payload: IUseRequestHandlerPayload[IUseRequestHandlerActionsKind];
}
